
import React from "react";
import { Modal, TextInput, Button, Group, Stack, useMantineColorScheme, LoadingOverlay, Box, Text } from "@mantine/core";

interface CreateFolderModalProps {
    opened: boolean;
    onClose: () => void;
    onSubmit: (name: string) => Promise<void>;
    loading?: boolean;
}

export function CreateFolderModal({
    opened,
    onClose,
    onSubmit,
    loading = false,
}: CreateFolderModalProps) {
    const { colorScheme } = useMantineColorScheme();
    const [name, setName] = React.useState("");
    const [error, setError] = React.useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) {
            setError("Folder name is required");
            return;
        }

        try {
            setError(null);
            await onSubmit(name.trim());
            setName("");
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to create folder");
        }
    };

    return (
        <Modal opened={opened} onClose={onClose} title="Create New Folder" centered overlayProps={{ backgroundOpacity: 0.1, blur: 2 }}>
            <LoadingOverlay visible={loading} overlayProps={{ backgroundOpacity: 0.1, blur: 2 }} />
            <form onSubmit={handleSubmit}>
                <Stack gap="md">
                    <Box
                        p="md"
                        style={{
                            border: '1px solid var(--modal-border)',
                            borderRadius: 'var(--mantine-radius-md)',
                            background: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.01)' : 'rgba(255, 255, 255, 0.5)',
                            backdropFilter: colorScheme === 'light' ? 'blur(10px)' : undefined
                        }}
                    >
                        <TextInput
                            label="Folder Name"
                            placeholder="Enter folder name"
                            value={name}
                            onChange={(e) => setName(e.currentTarget.value)}
                            error={error && " "}
                            required
                            data-autofocus
                        />
                    </Box>

                    {error && (
                        <Text c="red" size="sm" px="xs">
                            {error}
                        </Text>
                    )}

                    <Group justify="flex-end" pt="md">
                        <Button
                            variant={colorScheme === 'dark' ? 'light' : 'subtle'}
                            onClick={onClose}
                            disabled={loading}
                            color="gray"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            loading={loading}
                            variant="filled"
                            color={colorScheme === 'dark' ? 'gray.8' : 'brand'}
                        >
                            Create
                        </Button>
                    </Group>
                </Stack>
            </form>
        </Modal>
    );
}
